import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import gql from 'graphql-tag';
import { useStore } from '~/store';
import { findMachineAmongNavigationData } from '~/helpers/navigation';
import useAuth from '~/composables/useAuth';
import useDevice from '~/composables/useDevice';
import { useIsa95EquipmentsRef } from '~/composables/useEquipment';
import usePersistedPreferences from '~/composables/usePersistedPreferences';
import { ALL_PAGINATOR_INFO_FIELDS, EXTENDED_FACTORY_FIELDS } from '~/graphql/fragments';
import { apolloClient } from '~/plugins/apollo';

export default function useNavigation() {
  /**
   * Use store composable
   * @type {Store<any> | *}
   */
  const store = useStore();
  const router = useRouter();

  /**
  * i18n translator
  */
  const { t } = useI18n();
  const { activeUserIsSimplifiedOperator } = useAuth();
  const { isEventCreationPossibleWithoutDevice } = useDevice();
  const { modulePreferences } = usePersistedPreferences();

  /**
   * The selected manufacturing order in navigation
   */
  const selectedManufacturingOrderId = computed({
    get() {
      return store.state.navigation.selectedManufacturingOrder || 0;
    },
    set(value) {
      store.commit('navigation/SET_MANUFACTURING_ORDER', value);
    },
  });

  /**
   * The groups selected in navigation
   */
  const groupsSelected = computed({
    get() {
      return store.getters['navigation/groupsSelected'];
    },
    set(value) {
      // eslint-disable-next-line camelcase
      store.commit('navigation/setGroupsSelected', value);
    },
  });

  /**
   * Group selected in navigation
   */
  const groupSelected = computed({
    get() {
      return store.getters['navigation/groupSelected'];
    },
    set(value) {
      // eslint-disable-next-line camelcase
      store.commit('navigation/setGroupSelected', value?.group_id);
    },
  });

  /**
  * Factory selected in navigation
  */
  const selectedFactory = computed({
    get() {
      return store.getters['navigation/factorySelected'];
    },
    set(value) {
      // eslint-disable-next-line camelcase
      store.commit('navigation/setFactorySelected', value?.factory_id);
    },
  });

  /**
   * The machines associated to the selected group
   */
  const selectedGroupMachines = computed(() => groupSelected.value?.machines ?? []);

  /**
   * The machines associated to the selected group
   */
  const selectedGroupMachinesIds = computed(
    () => groupSelected.value?.machines?.map((machine) => machine.machine_id) ?? [],
  );

  /**
   * The resource selected in navigation or the one asked in the module configuration
   */
  const selectedMachine = computed(() => store.getters['navigation/machineSelected']);

  /**
  * The id of the resource selected in navigation or the one asked in the module configuration
  */
  const selectedMachineId = computed({
    get() {
      return store?.state?.navigation?.machineSelected;
    },
    set(value) {
      store.commit('navigation/setMachineSelected', value);
    },
  });

  /**
   * Return the state of the active machine.
   */
  const machineSelectedState = computed(() => selectedMachine.value?.machine_state);

  const isDeviceKeyVibe = computed(() => selectedMachine.value?.machine_devices[0]?.device_code === 'KeyVibe');

  const isDeviceKeynetic = computed(() => selectedMachine.value?.machine_devices[0]?.device_code === 'KeyNetic');

  const isDeviceCountTypeOnOff = computed(() => selectedMachine.value?.machine_count_type?.mct_name === 'on_off');

  /**
   * Return the selected device if found
   */
  const selectedDevice = computed(() => selectedMachine.value?.machine_devices[0]);

  /**
  * Tells if dashboard navigation is showed (tablet/mobile devices)
  */
  const showNavigation = computed({
    get() {
      return store.getters['navigation/getShowNavigation'];
    },
    set(value) {
      store.commit('navigation/setShowNavigation', value);
    },
  });

  const fetchNavigation = async (navigationType) => store.dispatch('navigation/fetchNavigation', navigationType);

  const periodTypeSelected = computed(() => store.getters['navigation/periodTypeSelected']);
  const openingHours = computed(() => store.getters['navigation/openingHours']);
  const resolvedPeriod = computed(() => store.getters['navigation/resolvedPeriod']);

  /**
   * Whether the period is still unknown, and therefore whether the KPI modules must hold
   * their query. Eight of them gate on this (ProdKPI, NewKPIEvolution, NewKPI, NewOutput,
   * OutputCount, NewTopLosses, LossesPareto, StatisticsChart), so a gate stuck closed
   * means those modules never load at all — not that they freeze.
   *
   * `openingHours` is a store key only the LEGACY selector writes
   * (`DashboardNavigationOpeningTimes.commitOpeningHours`). Under nav-next nothing writes
   * it — the equivalent state lives in `selectedTimeRange` — so the legacy condition
   * stayed true for ever in `Day` and the whole KPI surface of the machine dashboards
   * stayed empty (constat du 31/07 : muet en Jour, fonctionnel en Semaine, la seule
   * clause qui change entre les deux étant le type de période).
   *
   * Same bridge as `useNavigationBoundaries.getSelectedPeriodBoundaries`: nav-next first,
   * legacy as the fallback — one dialect, not a third.
   */
  const periodIsResolvedByNavNext = computed(() => Boolean(resolvedPeriod.value));

  const navigationPeriodNotLoaded = computed(
    () => !periodIsResolvedByNavNext.value
      && periodTypeSelected.value === 'Day'
      && !openingHours.value,
  );
  /**
   * List of changeovers available in navigation
   *
   * @returns {ComputedRef<boolean>}
   */
  const changeovers = computed(() => store.getters['navigation/changeovers']);

  const selectedChangeoverId = computed(() => store.getters['navigation/getSelectedChangeover']);

  const selectedChangeover = computed({
    get() {
      return store.state.navigation.selectedChangeover;
    },
    set(value) {
      store.commit('navigation/setSelectedChangeover', value);
    },
  });

  const selectedChangeoverObject = computed(() => {
    if (Array.isArray(store.state.navigation.changeovers)) {
      return store.state.navigation.changeovers.find(
        (ch) => ch.changeover_id === selectedChangeover.value,
      );
    }
    return undefined;
  });

  /**
   * Tells if the changeover is loaded.
   * Null-safe on `phaseExpand`: the list now also comes from nav-next (QA
   * KP-19626), and a changeover without phase must not break the consumers.
   */
  const changeoverLoaded = computed(() => changeovers.value.length === 0
    || (
      changeovers.value.length > 0
      && selectedChangeoverId.value !== 0
      && changeovers.value.find(
        (changeover) => parseInt(changeover.phaseExpand?.machine, 10) === selectedMachineId.value,
      )
    ));

  /**
   * Tells if the call to the module might be long
   */
  const potentialBigQuery = computed(() => ['Month', 'Year', 'Custom']
    .findIndex((v) => periodTypeSelected.value === v) >= 0);

  /**
  * Returns event/changeover wizard title
  *
  * @param i18nKey
  * @returns {string}
  */
  const setEventWizardTitle = (i18nKey) => t(i18nKey, {
    machine_name: selectedMachine.value?.machine_name,
  });

  /**
   * Keeps the module loading and avoid multiple apollo queries
   * @returns {boolean}
   */
  // Même garde que `navigationPeriodNotLoaded` sous un autre nom, avec ses propres
  // consommateurs (NewKPI, NewTopLosses, LossesPareto, NewKPIEvolution,
  // StatisticsChart) : elle doit recevoir le même pont, sinon la moitié des modules
  // reste muette. Le nom décrit la condition legacy et est conservé tel quel.
  const isPeriodTypeDayAndNoOpeningHours = computed(
    () => !periodIsResolvedByNavNext.value
      && periodTypeSelected.value === 'Day'
      && !openingHours.value,
  );

  /**
   * Do we display the shortcuts (Create event/Create changeover) in App bar
   */
  const showAppBarActionShortcuts = computed(() => store.state.navigation.showShortcuts);

  /**
   * Show group name when navigation type factory
   */
  const showGroups = computed(() => modulePreferences.value.globalKpiShowGroupsMode);

  /**
   * Actual source from DashboardNavigation
   */
  const selectedSource = computed(() => store.getters['navigation/selectedSource']);
  /**
   * Tells if the selected device in navigation is a Keynetic and has count activated
   */
  const isKeyneticWithCountEnabled = computed(() => (
    !!selectedMachine.value
      && selectedMachine.value.machine_count_type !== null
      && selectedMachine.value.machine_count_type !== undefined
      && selectedMachine.value.machine_count_type.mct_name !== 'on_off'
      && selectedMachine.value.machine_devices.length > 0
      && selectedMachine.value.machine_devices[0].device_code === 'KeyNetic'
  ));

  const isCountingTypeAndNotLastSelectedChangeover = computed(() => !isDeviceCountTypeOnOff.value
  && !selectedChangeoverObject.value?.is_last);

  /**
   * Tells if the selected machine can create events
   * @returns {boolean}
   */
  const canSelectedMachineCreateItems = computed(() => {
    // There is no selected machine, so we cannot create an event
    if (!selectedMachine.value) {
      return false;
    }

    // The selected machine has no device and the configuration requires a device to create events
    if (!isEventCreationPossibleWithoutDevice.value
          && (!Array.isArray(selectedMachine.value.machine_devices)
            || selectedMachine.value.machine_devices.length === 0)) {
      return false;
    }

    // All conditions are ok, we can create an event
    return true;
  });

  /**
   * Tells if the selected machine can create events
   * @returns {boolean}
   */
  const isEventCreationPossible = computed(() => canSelectedMachineCreateItems.value
   && !activeUserIsSimplifiedOperator.value);

  /**
   * Tells if the selected machine can create a changeover
   * @returns {boolean}
   */
  const isChangeoverCreationPossible = computed(() => canSelectedMachineCreateItems.value);

  /**
   * Checks if the machine has a changeover running
   * @returns {boolean}
   */
  const isChangeoverRunningForSelectedMachine = computed(() => {
    if (selectedMachine.value?.last_event) {
      return selectedMachine.value.last_event.event_end === null
      && selectedMachine.value.last_event.event_type === 22;
    }
    return false;
  });

  /**
   * Selected machines in navigation (only their ids)
   */
  const machinesSelected = computed({
    get() {
      return store.state.navigation.machinesSelected;
    },
    set(value) {
      // eslint-disable-next-line camelcase
      store.commit('navigation/setMachinesSelected', value);
    },
  });

  /**
   * An array of machines selected in navigation (whole objects)
   */
  const machinesSelectedObject = computed(
    () => machinesSelected.value.map(
      (machineId) => findMachineAmongNavigationData(
        machineId,
        store.state.navigation.navigationData,
      ),
    ),
  );

  /**
   * The workcenter of the selected machine
   */
  const selectedWorkcenter = computed(() => selectedMachine.value?.workcenter ?? null);

  /**
   * Tells if we are fetching changeovers from API
   */
  const fetchingChangeovers = computed(() => store.state.navigation.fetchingChangeovers);

  /**
   * Id of the selected group
   */
  const selectedGroupId = computed(() => store.state.navigation.groupSelected);

  /**
   * Id of the selected factory
   */
  const selectedFactoryId = computed(() => store.state.navigation.factorySelected);

  /**
   * Redirect to utilization dashboard and set navigation to group and machine selected
   */
  const redirectToUtilizationDashboard = async (machineId) => {
    const showGroupsMode = showGroups.value;
    const factorySelected = store.getters['navigation/factorySelected'];
    const useAreasAndWorkCenters = useIsa95EquipmentsRef.value;

    if (!showGroupsMode && !useAreasAndWorkCenters) {
      const foundGroup = factorySelected?.groups
        .find((g) => g.machines.find((m) => Number(m.machine_id) === Number(machineId)));

      if (foundGroup) {
        store.commit('navigation/setGroupSelected', Number(foundGroup.group_id));
      } else {
        let machineGroupId = null;
        let machineFactoryId = null;

        let allFactories = store.getters['factories/getFactories'];
        if (allFactories.length === 0) {
          const { data } = await apolloClient.query({
            query: gql`
              query Factories {
                factories(first: 200) {
                  data { ...ExtendedFactoryFields }
                  paginatorInfo { ...AllPaginatorInfoFields }
                }
              }
              ${EXTENDED_FACTORY_FIELDS}
              ${ALL_PAGINATOR_INFO_FIELDS}
            `,
          });
          store.commit('factories/commitFactories', data.factories.data);
          allFactories = store.getters['factories/getFactories'];
        }

        allFactories.forEach((factory) => {
          factory.groupsExpand.forEach((group) => {
            const foundMachine = group.machines
              .find((machine) => Number(machine.id) === Number(machineId));
            if (foundMachine) {
              machineGroupId = group.id;
              machineFactoryId = factory.id;
            }
          });
        });
        store.commit('navigation/setFactorySelected', Number(machineFactoryId));
        store.commit('navigation/setGroupSelected', Number(machineGroupId));
      }
    }

    store.commit('navigation/setMachineSelected', Number(machineId));
    router.push({ path: '/dashboard/1005' });
  };

  /**
   * The machines associated to the selected factory
   */
  const selectedFactoryMachines = computed(() => {
    if (selectedFactory.value !== null && Array.isArray(selectedFactory.value?.groups)) {
      // loops on the groups to get the machines
      const uniqueMachines = selectedFactory.value.groups.reduce((acc, group) => {
        group.machines.forEach((machine) => {
          // eslint-disable-next-line no-param-reassign
          acc[machine.machine_id] = machine;
        });
        return acc;
      }, {});

      return Object.values(uniqueMachines);
    }

    return [];
  });

  return {
    isChangeoverRunningForSelectedMachine,
    isDeviceKeynetic,
    isDeviceKeyVibe,
    isDeviceCountTypeOnOff,
    isEventCreationPossible,
    isChangeoverCreationPossible,
    isKeyneticWithCountEnabled,
    isCountingTypeAndNotLastSelectedChangeover,
    isPeriodTypeDayAndNoOpeningHours,
    machinesSelected,
    machinesSelectedObject,
    selectedFactoryMachines,
    selectedGroupMachines,
    selectedGroupMachinesIds,
    selectedMachine,
    selectedMachineId,
    machineSelectedState,
    showNavigation,
    fetchNavigation,
    changeoverLoaded,
    navigationPeriodNotLoaded,
    groupSelected,
    groupsSelected,
    potentialBigQuery,
    selectedDevice,
    selectedFactory,
    selectedWorkcenter,
    selectedManufacturingOrderId,
    setEventWizardTitle,
    showAppBarActionShortcuts,
    showGroups,
    selectedChangeoverId,
    selectedChangeover,
    selectedChangeoverObject,
    selectedSource,
    fetchingChangeovers,
    selectedGroupId,
    selectedFactoryId,
    redirectToUtilizationDashboard,
  };
}
